import type { TextureType } from '../types/heatmap.types';
import {
  barTrackOffsetYToValueInMeters,
  GRADIENT_STOP_BAND_OFFSET_PX,
  valueInMetersToBarHandleTopPx,
} from '../heatmap-stop-layout';

type BarLayoutStop = {
  readonly id: string;
  readonly value: string;
};

/** Minimum pointer movement before a handle interaction counts as a drag. */
export const STOP_HANDLE_DRAG_THRESHOLD_PX = 3;

export function pointerOffsetYOnTrack(
  clientY: number,
  trackTopPx: number
): number {
  return clientY - trackTopPx;
}

export function hasExceededDragThreshold(
  startClientY: number,
  currentClientY: number
): boolean {
  return (
    Math.abs(currentClientY - startClientY) > STOP_HANDLE_DRAG_THRESHOLD_PX
  );
}

/** Maps a pointer Y on the bar track to a stop value (handle-drag axis). */
export function pointerMappedValueAtClientY(
  clientY: number,
  stopsDesc: readonly BarLayoutStop[],
  trackTopPx: number,
  textureType: TextureType = 'gradient'
): number {
  return barTrackOffsetYToValueInMeters(
    clientY - trackTopPx,
    stopsDesc,
    textureType
  );
}

/** Inverse of {@link pointerMappedValueAtClientY}. */
export function clientYForPointerMappedValue(
  pointerMappedValueInMeters: number,
  stopsDesc: readonly BarLayoutStop[],
  trackTopPx: number,
  textureType: TextureType = 'gradient'
): number {
  const handleTop = valueInMetersToBarHandleTopPx(
    pointerMappedValueInMeters,
    stopsDesc,
    textureType
  );
  return trackTopPx + handleTop + GRADIENT_STOP_BAND_OFFSET_PX;
}

/** Offset between a stop's stored value and the value implied by the pointer. */
export function stopValueGrabOffsetInMeters(
  stopValueInMeters: number,
  pointerMappedValueInMeters: number
): number {
  return stopValueInMeters - pointerMappedValueInMeters;
}

/** Draft value while dragging, preserving the grab offset from pointer down. */
export function draftStopValueAtPointer(
  pointerMappedValueInMeters: number,
  valueGrabOffsetInMeters: number
): number {
  return pointerMappedValueInMeters + valueGrabOffsetInMeters;
}

/** Keeps handle drag pointer Y on the bar track so overflow inserts can be repositioned. */
export function clampPointerClientYToTrack(
  clientY: number,
  trackTopPx: number,
  trackHeightPx: number
): number {
  return Math.min(Math.max(clientY, trackTopPx), trackTopPx + trackHeightPx);
}

/** Pointer Y that yields the requested draft value after a grab at `grabClientY`. */
export function clientYForDraftValueAfterGrab(
  draftValueInMeters: number,
  grabClientY: number,
  stopValueInMeters: number,
  stopsDesc: readonly BarLayoutStop[],
  trackTopPx: number,
  textureType: TextureType = 'gradient'
): number {
  const grabPointerValue = pointerMappedValueAtClientY(
    grabClientY,
    stopsDesc,
    trackTopPx,
    textureType
  );
  const valueGrabOffset = stopValueGrabOffsetInMeters(
    stopValueInMeters,
    grabPointerValue
  );
  const targetPointerValue = draftValueInMeters - valueGrabOffset;
  return clientYForPointerMappedValue(
    targetPointerValue,
    stopsDesc,
    trackTopPx,
    textureType
  );
}

/** Handle top while dragging — follows the pointer from the grabbed position. */
export function handleTopPxDuringDrag(
  startHandleTopPx: number,
  startClientY: number,
  currentClientY: number
): number {
  return startHandleTopPx + (currentClientY - startClientY);
}
